// ── The Home chat's CONVERSATIONS (chat Figma rebuild WP5, frames 03, 04, 18, 33, 34; GAPS S5–S7) ──
//
// timelineSearch keeps the conversation on screen; this keeps the list it belongs to — the rail's
// rows. The server keeps one LIVE session per scope and archives the ones before it
// (`GET v1/chat/sessions?scope=home` → `{ live, recent[] }`, busy-main-bff#21); the rail lists them
// all, lets the user open a past one in the middle, and starts a new one from "+ New conversation".
//
//   • `recent`    the stored sessions as the server sent them (ChatSessionDto; WP6 adds
//                 `questionCount`, `lastMessageUtc`, `events[]` — every one optional), plus the ones
//                 archived from this tab, which are added here at once rather than after a reload;
//   • `live`      the live session as the server last described it — the live row's base, so a
//                 reloaded page still counts the questions asked before it;
//   • `viewingId` a PAST session shown in the middle. The live turns wait in `stash` meanwhile, and
//                 the next question puts them back first: asks always go to the live session;
//   • `pendingNewUtc` "+ New conversation" was pressed at that time (frame 34's stamp);
//   • `saving`    the conversation "Save conversation" is asking a destination for (busy-my-web#654):
//                 `{ id, title }` while the box's card is up, `busy` while the move is in flight.
//
// The rail's wording is lib/chatRail.js's; the stored → turn mapping is lib/chatSession.js's.

import api from '@/lib/services/general.service'
import { HOME_SCOPE, RESTORE_MESSAGES, homeConversation } from '@/lib/chatSession'
import { liveTurns, summaryFromTurns } from '@/lib/chatRail'
import { isLivePlaceholder, ongoingRoute, savedConversationText, SAVE_CONVERSATION_TEXT } from '@/lib/saveConversation'
import { toast } from '@/lib/smartToast'
import router from '@/router'

/** How many past conversations one page of the rail asks for. */
export const RECENT_PAGE = 10
/** How much of a past conversation is brought back when it is opened. */
export const OPEN_MESSAGES = 50
/** What the BFF answers in incognito for "a session that was not created". */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const unwrap = (res) => res?.data?.data ?? res?.data ?? null

export const state = {
  recent: [],
  take: RECENT_PAGE,
  hasMore: false,
  loadingMore: false,
  live: null,
  viewingId: null,
  stash: null,
  // Turns of sessions already brought back (or archived from this tab), by session id.
  turnsById: {},
  pendingNewUtc: null,
  saving: null,
  savingBusy: false,
}

export const getters = {
  recent: s => s.recent,
  hasMore: s => s.hasMore,
  loadingMore: s => s.loadingMore,
  live: s => s.live,
  viewingId: s => s.viewingId,
  stash: s => s.stash,
  pendingNewUtc: s => s.pendingNewUtc,
  saving: s => s.saving,
  savingBusy: s => s.savingBusy,
}

export const actions = {
  /** The `{ live, recent }` of `GET v1/chat/sessions` — timelineSearch/loadSession hands it over. */
  receive({ commit, state: s }, data = {}) {
    const recent = Array.isArray(data?.recent) ? data.recent : []
    commit('SET_LIVE', data?.live || null)
    commit('SET_RECENT', recent)
    commit('SET_HAS_MORE', recent.length >= s.take)
  },

  /** One more page of past conversations (the rail scrolled to its top). Silent on failure. */
  async loadMore({ commit, state: s }) {
    if (!s.hasMore || s.loadingMore) return
    commit('SET_LOADING_MORE', true)
    const take = s.take + RECENT_PAGE
    try {
      const data = unwrap(await api.get('chat/sessions', { scope: HOME_SCOPE, take, messages: RESTORE_MESSAGES })) || {}
      const recent = Array.isArray(data.recent) ? data.recent : []
      commit('SET_TAKE', take)
      commit('MERGE_RECENT', recent)
      commit('SET_HAS_MORE', recent.length >= take)
    } catch {
      commit('SET_HAS_MORE', false)
    } finally {
      commit('SET_LOADING_MORE', false)
    }
  },

  /**
   * Show a conversation in the middle (a click on its rail row).
   *
   * The live one (or the "Ready when you are" row) goes back to the live turns. A past one is
   * brought back with its blocks (`GET v1/chat/sessions/{id}/messages`, `blocksJson`) — once: a
   * second open reads what the first brought. A failed fetch leaves the screen as it was.
   */
  async open({ commit, dispatch, state: s, rootState }, id) {
    const key = id ? String(id) : ''
    const liveId = rootState?.timelineSearch?.sessionId
    if (!key || key === 'live' || key === 'pending' || (liveId && key === String(liveId))) {
      return dispatch('backToLive')
    }
    if (s.viewingId === key) return
    let turns = s.turnsById[key]
    if (!turns) {
      try {
        const data = unwrap(await api.get(`chat/sessions/${key}/messages`, { take: OPEN_MESSAGES }))
        turns = homeConversation(Array.isArray(data) ? data : (data?.messages || []))
        commit('CACHE_TURNS', { id: key, turns })
      } catch {
        return
      }
    }
    if (!s.viewingId) commit('SET_STASH', rootState?.timelineSearch?.conversation || [])
    commit('SET_VIEWING', key)
    commit('timelineSearch/SET_CONVERSATION', turns.map((t) => ({ ...t })), { root: true })
  },

  /** Put the live conversation back in the middle. Nothing to do when none was set aside. */
  backToLive({ commit, state: s }) {
    if (!s.viewingId) return
    commit('timelineSearch/SET_CONVERSATION', s.stash || [], { root: true })
    commit('SET_STASH', null)
    commit('SET_VIEWING', null)
  },

  /**
   * "+ New conversation" (frames 18, 33, 34).
   *
   * The live conversation is archived and listed with the past ones at once — its summary is built
   * from the turns on screen, so the row does not wait for a reload — and a new live session is
   * opened (`POST v1/chat/sessions`). The middle KEEPS the old thread: the next question is drawn
   * under a "New" divider (timelineSearch's `pendingBreak` → the turn's `sessionBreak`), and until
   * then the rail ends with "Ready when you are / New conversation".
   *
   * Server calls are for signed-in users only and every failure is silent: the new conversation
   * has started on screen whatever the store says, and the next ask opens a live session anyway.
   */
  async startNew({ commit, dispatch, state: s, rootState, rootGetters }) {
    if (s.viewingId) await dispatch('backToLive')
    const search = rootState?.timelineSearch || {}
    const turns = liveTurns(search.conversation)
    const oldId = search.sessionId || null
    const base = s.live && (!oldId || String(s.live.id) === String(oldId)) ? s.live : null
    const summary = summaryFromTurns(turns, { id: oldId || `local-${Date.now()}`, base, research: search.liveResearch })
    if (!summary) return
    commit('ADD_RECENT', { ...summary, edited: false })
    commit('CACHE_TURNS', { id: summary.id, turns: turns.map((t) => ({ ...t, sessionBreak: false })) })
    commit('timelineSearch/MARK_ARCHIVED', null, { root: true })
    commit('SET_LIVE', null)
    commit('SET_PENDING_NEW', new Date().toISOString())
    commit('timelineSearch/SET_PENDING_BREAK', true, { root: true })
    commit('timelineSearch/SET_LIVE_RESEARCH', null, { root: true })
    commit('timelineSearch/SET_SESSION', null, { root: true })
    if (!rootGetters?.['user/isAuthenticated']) return
    try {
      // Archive FIRST: opening while the old one is still live would hand the same session back.
      if (oldId) await api.toggle(`chat/sessions/${oldId}/archive`)
      const opened = unwrap(await api.insert('chat/sessions', { scope: HOME_SCOPE }))
      const id = opened?.id && opened.id !== EMPTY_GUID ? opened.id : null
      if (id && !rootState.timelineSearch.sessionId) commit('timelineSearch/SET_SESSION', id, { root: true })
    } catch {
      // The new conversation is on screen; the next ask files it under whatever session is live.
    }
  },

  /** The first question of a new conversation was asked — the "Ready when you are" row goes. */
  asked({ commit, state: s }) {
    if (s.pendingNewUtc) commit('SET_PENDING_NEW', null)
  },

  /**
   * "Save conversation" (busy-my-web#654) — a rail row's or the open conversation's. It ASKS first:
   * the box's card (SmartBox/SaveConversationCard.vue) reads `saving` and offers the destinations,
   * as the Research Pack save card does. The rules for who may are lib/saveConversation's.
   */
  promptSave({ commit }, { id, title } = {}) {
    if (!id) return
    commit('SET_SAVING', { id: String(id), title: String(title || '') })
  },
  cancelSave({ commit }) { commit('SET_SAVING', null) },

  /**
   * File the conversation under an Ongoing: `POST v1/chat/sessions/{id}/assign { ongoingId }`.
   *
   * `destination` is lib/saveConversation's `{ kind, id, title }`; a NEW Ongoing is made first,
   * through the create the app already has (`ongoing/createFromEntity`, origin research — what an
   * AI conversation is closest to). On success the row LEAVES the rail; if it was the live one,
   * Home starts over exactly as Clear does — minus the archive, since the session has already gone
   * to the Ongoing — and the next ask opens a fresh live session (the BFF's journal creates it).
   * The receipt is the app's toast with an Open that goes to the Ongoing. Answers the Ongoing, or
   * null on failure (the card stays up so the user can try again).
   */
  async saveToOngoing({ commit, dispatch, state: s, rootState }, destination) {
    const target = s.saving
    if (!target || s.savingBusy || !destination) return null
    commit('SET_SAVING_BUSY', true)
    try {
      const sessionId = await dispatch('resolveSessionId', target.id)
      if (!sessionId) throw new Error('no session')
      let ongoing = { id: destination.id, title: destination.title }
      if (destination.kind === 'new') {
        const made = await dispatch('ongoing/createFromEntity', { title: destination.title, origin: 'research' }, { root: true })
        if (!made?.id) throw new Error('no ongoing')
        ongoing = { id: made.id, title: made.title || destination.title }
      }
      const moved = unwrap(await api.insert(`chat/sessions/${sessionId}/assign`, { ongoingId: ongoing.id }))
      if (!moved?.id) throw new Error('not moved')

      const liveId = rootState?.timelineSearch?.sessionId
      const wasLive = isLivePlaceholder(target.id) || (liveId && String(liveId) === String(sessionId))
      if (s.viewingId && String(s.viewingId) === String(target.id)) await dispatch('backToLive')
      commit('REMOVE_RECENT', sessionId)
      if (wasLive) dispatch('leaveLive')

      commit('SET_SAVING', null)
      dispatch('ongoingChats/ensure', { ongoingId: ongoing.id, force: true }, { root: true })
      const to = ongoingRoute(ongoing.id)
      toast(savedConversationText(ongoing.title), {
        theme: 'success',
        autoClose: 6000,
        view: () => { if (to) router.push(to).catch(() => {}) },
        viewLabel: SAVE_CONVERSATION_TEXT.open,
      })
      return ongoing
    } catch {
      toast(SAVE_CONVERSATION_TEXT.failed, { theme: 'error', autoClose: 4000 })
      return null
    } finally {
      commit('SET_SAVING_BUSY', false)
    }
  },

  /**
   * The server id behind a rail row. The live row of a conversation begun in THIS tab has none on
   * the client — the ask path journals it after the answer — so it is read back from the scope's
   * live session, the way research.js's `recordSaved` finds its own.
   */
  async resolveSessionId({ rootState }, id) {
    if (!isLivePlaceholder(id)) return id ? String(id) : null
    const known = rootState?.timelineSearch?.sessionId
    if (known) return String(known)
    const data = unwrap(await api.get('chat/sessions', { scope: HOME_SCOPE }))
    return data?.live?.id ? String(data.live.id) : null
  },

  /** The live conversation went to an Ongoing: Home's middle starts a fresh one. */
  leaveLive({ commit, dispatch }) {
    commit('timelineSearch/CLEAR_CONVERSATION', null, { root: true })
    commit('timelineSearch/SET_PENDING_BREAK', false, { root: true })
    commit('timelineSearch/SET_LIVE_RESEARCH', null, { root: true })
    commit('timelineSearch/SET_TITLE', '', { root: true })
    commit('timelineSearch/SET_SESSION', null, { root: true })
    dispatch('reset')
  },

  /** The chat was cleared (timelineSearch/clearConversation): no past session stays open. */
  reset({ commit }) {
    commit('SET_STASH', null)
    commit('SET_VIEWING', null)
    commit('SET_PENDING_NEW', null)
    commit('SET_LIVE', null)
  },
}

const idOf = (session) => String(session?.id || '')

export const mutations = {
  SET_RECENT(s, list) { s.recent = Array.isArray(list) ? list.filter(idOf) : [] },
  /** A longer page replaces what it covers and keeps what only this tab knows (archived here). */
  MERGE_RECENT(s, list) {
    const incoming = (Array.isArray(list) ? list : []).filter(idOf)
    const known = new Set(incoming.map(idOf))
    s.recent = [...incoming, ...s.recent.filter((r) => !known.has(idOf(r)))]
  },
  ADD_RECENT(s, session) {
    if (!idOf(session)) return
    s.recent = [session, ...s.recent.filter((r) => idOf(r) !== idOf(session))]
  },
  REMOVE_RECENT(s, id) {
    const key = String(id || '')
    s.recent = s.recent.filter((r) => idOf(r) !== key)
    if (s.live && idOf(s.live) === key) s.live = null
    if (s.turnsById[key]) {
      const { [key]: _gone, ...rest } = s.turnsById // eslint-disable-line no-unused-vars
      s.turnsById = rest
    }
  },
  SET_SAVING(s, target) { s.saving = target || null },
  SET_SAVING_BUSY(s, v) { s.savingBusy = !!v },
  SET_TAKE(s, n) { s.take = n },
  SET_HAS_MORE(s, v) { s.hasMore = !!v },
  SET_LOADING_MORE(s, v) { s.loadingMore = !!v },
  SET_LIVE(s, live) { s.live = live || null },
  SET_VIEWING(s, id) { s.viewingId = id || null },
  SET_STASH(s, turns) { s.stash = Array.isArray(turns) ? turns : null },
  CACHE_TURNS(s, { id, turns }) { s.turnsById = { ...s.turnsById, [id]: turns } },
  SET_PENDING_NEW(s, at) { s.pendingNewUtc = at || null },
}
